import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, interval, take, takeUntil } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { AuthService } from '../../services/auth.service';
import { AuthenticationEvent } from '../../models/authentication-event.model';
import { AuthType } from '../../enums/auth.type.enum';
import { LoginPrompt } from '../../enums/login-prompt.enum';
import { UserService } from '../../services/user.service';
import { UserMessage } from '../error/user-message';
import { ErrorDisplayData } from '../../models/error-display-data.model';
import { ConsoleLogger } from '../console-logger';
import { PortalStateService } from '../../services/portal-state.service';

export type ReminderDialogRef = MatDialogRef<ReminderDialogComponent, void>;

@Component({
    selector: 'fss-portal-reminder-dialog',
    templateUrl: './reminder-dialog.component.html',
    styleUrls: ['./reminder-dialog.component.scss'],
    standalone: false
})
export class ReminderDialogComponent implements OnInit, OnDestroy {
  public timeLeft: number = 60;
  public isGuestAllow: boolean = false;
  public screenReaderAnnouncement: string = '';
  private unsubcriber$ = new Subject<void>();

  constructor(
    private readonly sessionService: SessionService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private dialogRef: MatDialogRef<ReminderDialogComponent>,
    private pss: PortalStateService) {
  }

  public ngOnInit() {
    ConsoleLogger.debug('^^^^ ReminderDialogComponent - ngOnInit - timeLeft: ' + this.timeLeft);
    this.authService.stopAccessExpCheck();
    this.sessionService.stopSessionChecks();
    const maxExpirationTimeInMillis = Date.now() + ((this.timeLeft + 2) * 1000); // Add 2s buffer
    ConsoleLogger.debug('^^^^ ReminderDialogComponent - ngOnInit - maxExpirationTimeInMillis: ' + maxExpirationTimeInMillis);

    interval(1000)
      .pipe(takeUntil(this.unsubcriber$))
      .subscribe(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0 || Date.now() >= maxExpirationTimeInMillis) {
          ConsoleLogger.debug('^^^^ ReminderDialogComponent - ngOnInit - time is up, logging out');
          this.authService.logFailInfo(undefined, {fn: 'reminderDialogTimeout', loc: 'countdown', maxExp: maxExpirationTimeInMillis, now: Date.now()}).pipe(take(1), takeUntil(this.unsubcriber$)).subscribe();
          this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.sessionExpiredMsg));
          this.dialogRef.close();
        } else if (this.timeLeft === 59) {
          this.screenReaderAnnouncement = 'Your session will expire in 1 minute.';
        } else if (this.timeLeft === 10) {
          this.screenReaderAnnouncement = 'Your session will expire in 10 seconds.';
        }
      });

    const siteGuid = this.authService.getSiteGuid();
    if (siteGuid) {
      const site = this.userService.getSite(siteGuid);
      this.isGuestAllow = site && site.guestAllowed;
    }
  }

  public ngOnDestroy(): void {
    ConsoleLogger.debug('^^^^ ReminderDialogComponent - ngOnDestroy');
    this.unsubcriber$.next();
    this.unsubcriber$.complete();
  }

  public stayLogin() {
    ConsoleLogger.debug('^^^^ ReminderDialogComponent - stayLogin()');
    this.sessionService.updateUserSessionExpirationTime(this.pss.getActiveApp()?.app);
    this.sessionService.startSessionChecks();
    this.authService.startAccessExpCheck(this.authService.getGrants()?.role?.sessionTimeoutMillis);
    this.dialogRef.close();
  }

  public logout() {
    ConsoleLogger.debug('^^^^ ReminderDialogComponent - logout()');
    this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.LOGOUT, undefined, 'reminder-logout'));
    this.dialogRef.close();
  }
}
